/**
 * Referral Tracking Utility
 *
 * Implements triple-tracking for referral attribution:
 * 1. URL parameter (?ref=CODE) - highest priority
 * 2. localStorage - persists across sessions
 * 3. Cookie (arml_ref) - set by /r/:code redirect on backend
 *
 * Priority: URL param > localStorage > cookie
 *
 * @module lib/referral-tracking
 *
 * @example
 * // Get referral code (reads from all sources with priority)
 * const code = getReferralCode();
 *
 * // Capture from URL and persist (call on page load)
 * captureReferralFromUrl();
 *
 * // Clear after successful signup/checkout
 * clearReferralCode();
 */

const STORAGE_KEY = 'arml_referral_code';
const COOKIE_NAME = 'arml_ref';

/**
 * Get the referral code from URL search params
 */
function getFromUrl(): string | null {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  return params.get('ref');
}

/**
 * Get the referral code from localStorage
 */
function getFromStorage(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * Get the referral code from cookie
 */
function getFromCookie(): string | null {
  if (typeof document === 'undefined') return null;
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === COOKIE_NAME && value) {
      return decodeURIComponent(value);
    }
  }
  return null;
}

/**
 * Save referral code to localStorage
 */
function saveToStorage(code: string): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY, code);
  } catch {
    // Storage might be full or disabled
  }
}

/**
 * Get the referral code from any source with priority:
 * URL param > localStorage > cookie
 *
 * @returns The referral code if found, null otherwise
 */
export function getReferralCode(): string | null {
  // Priority 1: URL parameter (fresh click)
  const fromUrl = getFromUrl();
  if (fromUrl) return fromUrl;

  // Priority 2: localStorage (persisted from previous visit)
  const fromStorage = getFromStorage();
  if (fromStorage) return fromStorage;

  // Priority 3: Cookie (set by /r/:code redirect)
  const fromCookie = getFromCookie();
  if (fromCookie) return fromCookie;

  return null;
}

/**
 * Capture referral code from URL and persist to localStorage
 * Call this on app initialization to persist the referral
 *
 * @returns The captured code if found in URL, null otherwise
 */
export function captureReferralFromUrl(): string | null {
  const code = getFromUrl();
  if (code) {
    saveToStorage(code);
    return code;
  }
  return null;
}

/**
 * Set a referral code manually (e.g., from a form input)
 *
 * @param code - The referral code to set
 */
export function setReferralCode(code: string): void {
  saveToStorage(code);
}

/**
 * Clear the referral code from localStorage
 * Call this after successful signup/checkout
 */
export function clearReferralCode(): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Ignore errors
  }
}

/**
 * Check if there's an active referral tracking
 *
 * @returns true if a referral code exists in any source
 */
export function hasReferralCode(): boolean {
  return getReferralCode() !== null;
}
